import { faBullseyeArrow } from '@fortawesome/pro-light-svg-icons'
import {
  faBan,
  faCircleUserCirclePlus,
  faTag,
  faTriangleExclamation
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from '@tanstack/react-router'
import clsx from 'clsx'
import { useState } from 'react'
import { editorDisplay } from '~/components/Form/Input/EditorInput/EditorInput.css'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { NameCircle } from '~/components/Global/Elements/NameCircle/NameCircle'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { useApplicantPicture } from '~/domains/applicants/queries/applicantPictures'
import { useApplicantSafetyCheck } from '~/domains/applicants/queries/applicantSafetyChecks'
import type {
  ApplicantHeadData,
  ApplicantMatchingData
} from '~/domains/applicants/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { OrderApplicantComments } from '~/domains/orders/components/OrderApplicantComments'
import { useDebugMode } from '~/hooks/useDebugMode'
import { profilePicture40 } from '~/styles/picture.css'
import { vars } from '~/styles/theme.css'
import { formatRelativeTime, isHtmlEmpty } from '~/utils'
import { formatDateShort } from '~/utils/date'
import { formatName } from '~/utils/names'
import {
  applicantHoverHeader,
  applicantHoverHeaderInfo,
  applicantHoverHeaderMetaBadge,
  applicantHoverHeaderMetaItem,
  applicantHoverHeaderMetas,
  applicantHoverHeaderName,
  applicantHoverList,
  applicantHoverListItem,
  applicantHoverListItemHeader,
  applicantHoverListItemMuted,
  applicantHoverListItemSuccess,
  applicantHoverNote,
  applicantHoverNoteContent,
  applicantHoverNoteContentFull,
  applicantHoverNoteContentFullTrigger,
  applicantHoverPicture
} from './ApplicantCardHoverContent.css'

const MATCHING_STRENGTH_LABELS: Record<string, string> = {
  'qualifications.full': 'Toutes les qualifications correspondent',
  'qualifications.partial': 'Qualifications en partie correspondantes',
  'worked.company': 'A déjà travaillé pour cette entreprise',
  'worked.order': 'A déjà travaillé pour nous',
  'location.mobility': 'Mobilité compatible avec la localisation',
  'location.radius': 'Habite dans le rayon de la commande',
  'location.radius_far': 'Habite à proximité de la commande',
  authorizations: 'Habilitations correspondantes',
  'availability.max': 'Disponible récemment',
  'availability.partial': 'Disponibilité connue récente',
  'languages.full': 'Toutes les langues requises',
  'languages.partial': 'Langues en partie correspondantes',
  'softwares.full': 'Tous les logiciels requis',
  'softwares.partial': 'Logiciels en partie correspondants',
  segmentation: 'Segmentation correspondante',
  education: "Niveau d'études conforme",
  experience: 'Expérience professionnelle conforme'
}

export type ApplicantHoverData = ApplicantHeadData &
  ApplicantMatchingData & {
    created?: string
    note?: string
  }

export const ApplicantCardHoverContent = ({
  applicant,
  contextOrderId,
  showScore = true,
  showSafetyCheck = true,
  showNewApplicantBanner = false
}: {
  applicant: ApplicantHoverData
  /** Excludes the current order from the "already on another open order" warning */
  contextOrderId?: number
  showScore?: boolean
  showSafetyCheck?: boolean
  showNewApplicantBanner?: boolean
}) => {
  const [isNoteContentFull, setIsNoteContentFull] = useState(false)
  const isDebug = useDebugMode()
  const { data: legacyUrl } = useConfig('organization.legacy_url')
  const { data: consideredNewDelay } = useConfig(
    'applicant.considered_new_delay'
  )
  const { data: applicantPicture } = useApplicantPicture({
    applicantId: applicant.id
  })
  const { data: safetyCheck } = useApplicantSafetyCheck({
    applicantId: applicant.id,
    enabled: showSafetyCheck
  })

  const applicantHead = applicantPicture?.applicant ?? applicant
  const formattedName = formatName(applicantHead)
  const rawPicUrl = applicantPicture?.profilePicUrl
  const pictureUrl =
    rawPicUrl && rawPicUrl.indexOf('/admin') !== -1
      ? `${legacyUrl ?? ''}${rawPicUrl}`
      : rawPicUrl

  const lastActionTime =
    applicantHead.lastAction?.completionDate ??
    applicantHead.lastAction?.scheduledDate ??
    applicantHead.lastAction?.created

  const isNewApplicant =
    applicant.created &&
    consideredNewDelay &&
    Date.now() - new Date(applicant.created).getTime() <=
      consideredNewDelay * 7 * 24 * 60 * 60 * 1000 // delay in weeks

  const openOrders =
    safetyCheck?.openOrders.filter((order) => order.id !== contextOrderId) ?? []
  const missingDocuments = safetyCheck?.missingDocuments ?? []
  const expiredDocuments = safetyCheck?.expiredDocuments ?? []

  return (
    <>
      <div className={applicantHoverHeader}>
        <div className={applicantHoverPicture}>
          {pictureUrl ? (
            <img
              src={pictureUrl}
              className={profilePicture40}
              alt={formattedName}
              draggable={false}
            />
          ) : (
            <NameCircle name={formattedName} size={40} />
          )}
        </div>
        <div className={applicantHoverHeaderInfo}>
          <span className={applicantHoverHeaderName}>{formattedName}</span>
          <div className={applicantHoverHeaderMetas}>
            {applicant.status?.name && (
              <Badge
                title={applicant.status.name}
                backgroundColor={applicant.status.color ?? ''}
                badgeClass={clsx(
                  applicantHoverHeaderMetaBadge,
                  applicantHoverHeaderMetaItem
                )}
              />
            )}
            {showNewApplicantBanner && isNewApplicant && (
              <span
                className={applicantHoverHeaderMetaItem}
                style={{ color: vars.color.success }}
              >
                <FontAwesomeIcon icon={faCircleUserCirclePlus} />
                Nouveau candidat
              </span>
            )}
            {lastActionTime && (
              <Tooltip asChild text="Dernière action">
                <span className={applicantHoverHeaderMetaItem}>
                  <FontAwesomeIcon icon={faBullseyeArrow} />
                  {formatRelativeTime(new Date(lastActionTime))}
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {applicant.qualifications && applicant.qualifications.length > 0 && (
        <div className={applicantHoverList}>
          {applicant.qualifications.map((qualification) => (
            <span key={qualification.id} className={applicantHoverListItem}>
              <FontAwesomeIcon icon={faTag} />
              {qualification.name}
            </span>
          ))}
        </div>
      )}
      {showScore && applicant.matchingScore !== undefined && (
        <div className={applicantHoverList}>
          {isDebug && applicant.matchingScoreDetails && (
            <>
              <div
                className={clsx(
                  applicantHoverListItem,
                  applicantHoverListItemHeader
                )}
              >
                Scoring détaillé ({applicant.matchingScore}
                {applicant.matchingScoreMax !== undefined
                  ? `/${applicant.matchingScoreMax}`
                  : ''}{' '}
                — {applicant.matchingScorePercentage}%) :
              </div>
              {Object.entries(applicant.matchingScoreDetails).map(
                ([key, value]) => (
                  <div
                    key={key}
                    className={clsx(
                      applicantHoverListItem,
                      value
                        ? applicantHoverListItemSuccess
                        : applicantHoverListItemMuted
                    )}
                  >
                    {key} : {value ?? 0}
                  </div>
                )
              )}
            </>
          )}
          {applicant.matchingStrengths &&
            applicant.matchingStrengths.length > 0 && (
              <>
                <div
                  className={clsx(
                    applicantHoverListItem,
                    applicantHoverListItemHeader
                  )}
                >
                  Points forts :
                </div>
                {applicant.matchingStrengths.map((strength) => (
                  <div
                    key={strength}
                    className={clsx(
                      applicantHoverListItem,
                      applicantHoverListItemSuccess
                    )}
                  >
                    ✓ {MATCHING_STRENGTH_LABELS[strength] ?? strength}
                  </div>
                ))}
              </>
            )}
        </div>
      )}
      {showSafetyCheck && safetyCheck && (
        <div className={applicantHoverList}>
          {openOrders.length > 0 && (
            <div className={applicantHoverListItem}>
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={{ color: vars.color.warning }}
              />
              Déjà présent sur{' '}
              {openOrders.length === 1
                ? 'une autre commande ouverte : '
                : `${openOrders.length} autres commandes ouvertes : `}
            </div>
          )}
          {openOrders.map((order) => (
            <Link
              key={order.id}
              to="/orders/$orderId"
              params={{ orderId: String(order.id) }}
              className={applicantHoverListItem}
            >
              {order.name}
            </Link>
          ))}
          {missingDocuments.length > 0 && (
            <div className={applicantHoverListItem}>
              <FontAwesomeIcon
                icon={faBan}
                style={{ color: vars.color.danger }}
              />
              {missingDocuments.length === 1
                ? 'Document manquant : '
                : `${missingDocuments.length} documents manquants : `}
              {missingDocuments.join(', ')}
            </div>
          )}
          {expiredDocuments.length > 0 && (
            <div className={applicantHoverListItem}>
              <FontAwesomeIcon
                icon={faBan}
                style={{ color: vars.color.danger }}
              />
              {expiredDocuments.length === 1
                ? 'Document expiré : '
                : `${expiredDocuments.length} documents expirés : `}
              {expiredDocuments
                .map(
                  (doc) =>
                    `${doc.type} (expiré le ${formatDateShort(new Date(doc.expirationDate))})`
                )
                .join(', ')}
            </div>
          )}
        </div>
      )}
      <OrderApplicantComments
        orderId={contextOrderId}
        applicantId={applicant.id}
        internalNote={
          applicant.note && !isHtmlEmpty(applicant.note) ? (
            <div className={applicantHoverNote}>
              <div
                className={clsx(
                  applicantHoverNoteContent,
                  editorDisplay,
                  isNoteContentFull && applicantHoverNoteContentFull
                )}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: needed for rich text content display
                dangerouslySetInnerHTML={{ __html: applicant.note }}
              />
              {applicant.note.length > 200 && !isNoteContentFull && (
                <button
                  type="button"
                  className={applicantHoverNoteContentFullTrigger}
                  onClick={() => setIsNoteContentFull(true)}
                >
                  (Voir plus)
                </button>
              )}
            </div>
          ) : null
        }
      />
    </>
  )
}
